<template>
  <Teleport v-if="!isRenderedDetached" :to="'#bk-sidebar-tabs-' + region">
    <button
      :id="'bk-sidebar-button-' + id"
      ref="tourElement"
      class="bk-toolbar-button"
      :class="[{ 'is-active': activeSidebar === id }, 'bk-is-' + region]"
      :disabled="isDisabled"
      :style="{ order: weight }"
      @click.prevent.stop="toggleSidebar"
    >
      <slot name="icon">
        <Icon v-if="icon" :name="icon" />
      </slot>
      <slot name="badge" />
      <div class="bk-tooltip">
        <span>{{ title }}</span>
        <ShortcutIndicator
          v-if="keyCode"
          :meta="meta"
          :shift="shift"
          :key-code="keyCode"
          :label="title"
          @pressed="toggleSidebar"
        />
      </div>
    </button>
  </Teleport>

  <Teleport
    v-if="
  (activeSidebar === id || isRenderedDetached || renderAlways) && !isDisabled
"
    :to="isRenderedDetached ? 'body' : '#bk-sidebar-content-' + region"
  >
    <SidebarDetached
      v-if="isRenderedDetached"
      :id="id"
      ref="tourElement"
      :title="title"
      :icon="icon"
      :min-width="minWidth"
      :min-height="minHeight"
      :size="size"
      :is-left="region === 'left'"
      class="bk-sidebar-inner"
      @close="onAttach"
    >
      <template #icon>
        <slot name="icon" />
      </template>
      <template #default="{ width, height, isResizing }">
        <div class="bk-sidebar-content-wrapper">
          <div ref="sidebarContent" class="bk-sidebar-content">
            <slot
              :key="isRenderedDetached ? 'detached' : 'attached'"
              :scrolled-to-end="scrolledToEnd"
              :is-detached="isRenderedDetached"
              :width="width"
              :height="height"
              :toggle-sidebar="toggleSidebar"
              :is-resizing="isResizing"
            />
          </div>
        </div>
      </template>
    </SidebarDetached>
    <ScrollBoundary
      v-else
      v-show="activeSidebar === id"
      class="bk-sidebar-inner"
    >
      <div class="bk">
        <div class="bk-sidebar-title">
          <span>{{ title }}</span>
          <div v-if="beta" class="bk-beta-indicator">BETA</div>
          <button v-if="!ui.isMobile.value" @click.prevent.stop="onDetach">
            <Icon name="expand" />
          </button>
          <button @click.prevent.stop="toggleSidebar">
            <Icon name="close" />
          </button>
        </div>
      </div>
      <div class="bk-sidebar-content-wrapper">
        <div ref="sidebarContent" class="bk-sidebar-content">
          <slot
            :key="isRenderedDetached ? 'detached' : 'attached'"
            :scrolled-to-end="scrolledToEnd"
            :is-detached="isRenderedDetached"
            :width="undefined"
            :height="undefined"
            :toggle-sidebar="toggleSidebar"
            :is-resizing="false"
          />
        </div>
      </div>
    </ScrollBoundary>
  </Teleport>
</template>

<script setup>
import { computed, watch, ref, useBlokkli } from "#imports";
import { Icon, ShortcutIndicator, ScrollBoundary } from "#blokkli/components";
import SidebarDetached from "./Detached/index.vue";
import defineCommands from "#blokkli/helpers/composables/defineCommands";
import onBlokkliEvent from "#blokkli/helpers/composables/onBlokkliEvent";
import useAnimationFrame from "#blokkli/helpers/composables/useAnimationFrame";
import defineTourItem from "#blokkli/helpers/composables/defineTourItem";
const props = defineProps({
  id: { type: String, required: true },
  title: { type: String, required: true },
  tourText: { type: String, required: false, default: void 0 },
  editOnly: { type: Boolean, required: false },
  icon: { type: null, required: true },
  weight: { type: [String, Number], required: false, default: 0 },
  renderAlways: { type: Boolean, required: false },
  disabled: { type: Boolean, required: false },
  region: { type: String, required: false, default: "right" },
  minWidth: { type: Number, required: false, default: void 0 },
  minHeight: { type: Number, required: false, default: void 0 },
  size: { type: Object, required: false, default: void 0 },
  meta: { type: Boolean, required: false },
  shift: { type: Boolean, required: false },
  keyCode: { type: String, required: false, default: void 0 },
  beta: { type: Boolean, required: false }
});
const emit = defineEmits(["updated"]);
const { storage, state, ui, $t } = useBlokkli();
const tourElement = ref(null);
const detachedKey = computed(() => "sidebar:detached:" + props.id);
const storageKey = computed(() => "sidebar:active:" + props.region);
const isDetached = storage.use(detachedKey, false);
const isDisabled = computed(
  () => props.editOnly && state.editMode.value !== "editing" && !props.disabled
);
const activeSidebar = storage.use(storageKey, "");
const isRenderedDetached = computed(
  () => isDetached.value && !ui.isMobile.value
);
watch(isDisabled, (v) => {
  if (v && activeSidebar.value === props.id) {
    activeSidebar.value = "";
  }
});
const onDetach = () => {
  isDetached.value = true;
  activeSidebar.value = "";
  emit("updated");
};
const onAttach = () => {
  isDetached.value = false;
  emit("updated");
};
const toggleSidebar = () => {
  if (isDetached.value) {
    isDetached.value = false;
    return;
  }
  activeSidebar.value = activeSidebar.value === props.id ? "" : props.id;
  emit("updated");
};
const showSidebar = () => {
  if (isDetached.value) {
    return;
  }
  activeSidebar.value = props.id;
};
watch(activeSidebar, (active) => {
  if (active) {
    document.documentElement.classList.add("bk-has-sidebar-" + props.region);
  } else {
    document.documentElement.classList.remove("bk-has-sidebar-" + props.region);
  }
});
const sidebarContent = ref(null);
const scrolledToEnd = ref(false);
const isOverflowing = ref(false);
useAnimationFrame(() => {
  if (sidebarContent.value) {
    scrolledToEnd.value = sidebarContent.value.scrollHeight - (sidebarContent.value.scrollTop + sidebarContent.value.offsetHeight) < 3;
    isOverflowing.value = sidebarContent.value.scrollHeight > sidebarContent.value.offsetHeight;
  }
});
const commandTitle = computed(() => {
  if (activeSidebar.value === props.id) {
    return $t("sidebar.hide", "Hide @title").replace("@title", props.title);
  }
  return $t("sidebar.show", "Show @title").replace("@title", props.title);
});
const commandCallback = () => {
  if (activeSidebar.value === props.id) {
    activeSidebar.value = "";
  } else {
    activeSidebar.value = props.id;
  }
};
defineCommands(() => {
  if (isDisabled.value) {
    return;
  }
  return {
    id: "plugin:sidebar:" + props.id,
    label: commandTitle.value,
    group: "ui",
    icon: props.icon,
    disabled: isRenderedDetached.value,
    callback: commandCallback
  };
});
onBlokkliEvent("item:dropped", () => {
  if (ui.isMobile.value && activeSidebar.value) {
    activeSidebar.value = "";
  }
});
onBlokkliEvent("sidebar:open", (id) => {
  if (id === props.id) {
    activeSidebar.value = props.id;
  }
});
defineExpose({ showSidebar });
defineTourItem(() => {
  if (!props.tourText) {
    return;
  }
  return {
    id: "plugin:sidebar:" + props.id,
    title: props.title,
    text: props.tourText,
    element: () => tourElement.value
  };
});
</script>

<script>
export default {
  name: "PluginSidebar"
};
</script>
